import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import ProfileHeader from '../../components/ProfileHeader';
import { useProfile } from '../../hooks/useProfile';
import { useFollowing } from '../../hooks/useFollowing';
import { useAuth } from '../../../../core/providers/AuthProvider';
import { usePreferences } from '../../../../shared/hooks/usePreferences';
import { Spinner } from '../../../../core/design-system/components/Spinner';
import Avatar from '../../../../core/design-system/components/Avatar';
import './styles.css';
import FollowersModal from '../../components/FollowersModal';
import ProfileRestricted from '../../components/ProfileRestricted';
import { useFollowRequests } from '../../hooks/useFollowRequests';
import { useNotification } from '../../../../shared/hooks/useNotification';

const ProfilePage = () => {
  const { t: tProfile } = useTranslation('profile');
  const { uid: paramUid } = useParams();
  const { user: currentUser } = useAuth();
  const [preferencesCorrected, setPreferencesCorrected] = useState(false);
  const { get: getPreference } = usePreferences();
  
  const uid = paramUid || currentUser?.uid;
  
  const { 
    user, 
    loading, 
    error,
    isOwnProfile,
    refresh
  } = useProfile(uid);

  const { 
    isFollowing, 
    isPending, 
    isLoading, 
    followUser, 
    unfollowUser, 
    cancelRequest,
    refreshCurrentUser
  } = useFollowing(uid);
  
  const { acceptFollowRequest, rejectFollowRequest } = useFollowRequests();
  
  const notification = useNotification('profile');
  
  const [modalOpen, setModalOpen] = useState(false);
  const [modalType, setModalType] = useState('followers');
  
  const handleFollow = async () => {
    if (!isFollowing && !isPending && followUser) {
      try {
        await followUser();
        if (isOwnProfile) await refreshCurrentUser();
        await refresh();
        notification.success(tProfile('toast.follow_success'));
      } catch (error) {
        notification.error(tProfile('toast.follow_error'));
      }
    }
  };
  
  const handleUnfollow = async () => {
    if (isFollowing && unfollowUser) {
      try {
        await unfollowUser();
        if (isOwnProfile) await refreshCurrentUser();
        await refresh();
        notification.success(tProfile('toast.unfollow_success'));
      } catch (error) {
        notification.error(tProfile('toast.unfollow_error'));
      }
    }
  };
  
  const handleCancelRequest = async () => {
    if (isPending && cancelRequest) {
      try {
        await cancelRequest();
        if (isOwnProfile) await refreshCurrentUser();
        await refresh();
        notification.success(tProfile('toast.cancel_success'));
      } catch (error) {
        notification.error(tProfile('toast.cancel_error'));
      }
    }
  };
  
  const handleViewFollowers = () => {
    setModalType('followers');
    setModalOpen(true);
  };
  
  const handleViewFollowing = () => {
    setModalType('following');
    setModalOpen(true);
  };
  
  const handleViewRequests = () => {
    setModalType('requests');
    setModalOpen(true);
  };
  
  // Fonction pour vérifier et corriger les préférences
  useEffect(() => {
    const checkAndFixPreferences = async () => {
      if (user && isOwnProfile && !preferencesCorrected) {
        try {
          const profileVisibilityPref = getPreference('profileVisibility');
          
          if (profileVisibilityPref && 
              profileVisibilityPref !== user.preferences?.profileVisibility) {
            setPreferencesCorrected(true);
            refresh();
          } else {
            setPreferencesCorrected(true);
          }
        } catch (error) {
          console.error("Erreur lors de la vérification des préférences:", error);
        }
      }
    };
    
    checkAndFixPreferences();
  }, [user, isOwnProfile, getPreference, preferencesCorrected, refresh]);
  
  const userPreferences = user?.preferences || {};
  const profileVisibility = userPreferences.profileVisibility || 'everyone';

  const canViewProfile = 
    isOwnProfile ||
    (profileVisibility === 'everyone') ||
    (profileVisibility === 'followers' && isFollowing);
  
  const handleRequestsChange = useCallback(async () => {
    await refresh();
  }, [refresh]);

  const hasReceivedFollowRequest = currentUser?.followRequests?.includes(user?.uid);

  const handleAcceptFollow = async () => {
    try {
      await acceptFollowRequest(user.uid);
      await refresh();
      notification.success(tProfile('toast.accept_success'));
    } catch (error) {
      notification.error(tProfile('toast.accept_error'));
    }
  };
  
  const handleRejectFollow = async () => {
    try {
      await rejectFollowRequest(user.uid);
      await refresh();
      notification.success(tProfile('toast.reject_success'));
    } catch (error) {
      notification.error(tProfile('toast.reject_error'));
    }
  };

  if (!uid) {
    return <div className="error-message">{tProfile('errors.not_found')}</div>;
  }

  if (loading) {
    return <Spinner />;
  }

  if (error) {
    return <div className="error-message">{error}</div>;
  }

  if (!user) {
    return <div className="error-message">{tProfile('errors.user_not_found')}</div>;
  }

  // Si l'utilisateur n'a pas le droit de voir le profil complet
  if (!canViewProfile) {
    return (
      <div className="profile-page">
        {/* Actions de follow pour les profils restreints */}
        {!isOwnProfile && (
          <div className="profile-actions-bar">
            {hasReceivedFollowRequest ? (
              <>
                <button className="follow-button accept" onClick={handleAcceptFollow}>
                  {tProfile('actions.accept')}
                </button>
                <button className="follow-button reject" onClick={handleRejectFollow}>
                  {tProfile('actions.reject')}
                </button>
              </>
            ) : isFollowing ? (
              <button className="follow-button following" onClick={handleUnfollow}>
                {tProfile('actions.unfollow')}
              </button>
            ) : isPending ? (
              <button className="follow-button pending" onClick={handleCancelRequest}>
                {tProfile('actions.cancel_request')}
              </button>
            ) : (
              <button className="follow-button" onClick={handleFollow}>
                {tProfile('actions.follow')}
              </button>
            )}
          </div>
        )}
        
        {/* Afficher uniquement la photo, le nom et le niveau */}
        <div className="profile-header-minimal">
          <div className="profile-avatar-container">
            <Avatar 
              text={user.displayName?.charAt(0)?.toUpperCase() || '?'}
              src={user.photoURL}
              size="xl"
              className="profile-avatar"
            />
          </div>
          <div className="profile-name-level">
            <h1 className="profile-name">{user.displayName}</h1>
            <span className="profile-level">{user.level || "Débutant"}</span>
          </div>
        </div>
        
        <ProfileRestricted type={profileVisibility === 'followers' ? 'followers' : 'private'} />
      </div>
    );
  }

  return (
    <div className="profile-page">
      {/* Actions de follow pour les autres profils */}
      {!isOwnProfile && (
        <div className="profile-actions-bar">
          {hasReceivedFollowRequest ? (
            <>
              <button className="follow-button accept" onClick={handleAcceptFollow}>
                {tProfile('actions.accept')}
              </button>
              <button className="follow-button reject" onClick={handleRejectFollow}>
                {tProfile('actions.reject')}
              </button>
            </>
          ) : isFollowing ? (
            <button className="follow-button following" onClick={handleUnfollow}>
              {tProfile('actions.unfollow')}
            </button>
          ) : isPending ? (
            <button className="follow-button pending" onClick={handleCancelRequest}>
              {tProfile('actions.cancel_request')}
            </button>
          ) : (
            <button className="follow-button" onClick={handleFollow}>
              {tProfile('actions.follow')}
            </button>
          )}
        </div>
      )}
      
      <ProfileHeader 
        user={user}
        isFollowing={isFollowing}
        isPending={isPending}
        isLoading={isLoading}
        onFollow={handleFollow}
        onUnfollow={handleUnfollow}
        onCancelRequest={handleCancelRequest}
        onProfileUpdate={refresh}
        onViewFollowers={handleViewFollowers}
        onViewFollowing={handleViewFollowing}
        onViewRequests={handleViewRequests}
        restrictedView={!isOwnProfile && (
          profileVisibility === 'nobody' || 
          (profileVisibility === 'followers' && !isFollowing)
        )}
      />

      <FollowersModal 
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        userId={uid}
        type={modalType}
        onRequestsChange={handleRequestsChange}
      />
    </div>
  );
};

export default ProfilePage;
